/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
 * Navigation History Slice
 *
 * Centralized Redux state for tracking route transitions, enabling custom back-navigation
 * and storing a temporary savedEntityId used for deferred fetch flows.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RoutesConstants } from '@malmur-technologies/medicalsystem-shared';

/**
 * Interface representing a single navigation history entry.
 * Tracks route visits with unique keys, state, and timestamps for navigation management.
 */
export interface RouteHistoryEntry {
  route: string;
  key: string;
  state?: Record<string, unknown>;
  timestamp: number;
}

/**
 * Redux state interface for navigation history management.
 * Maintains a stack of visited routes with optional entity ID persistence for deferred fetching.
 */
interface NavigationHistoryState {
  history: RouteHistoryEntry[];
  savedEntityId: string | null;
  maxHistorySize: number;
}

const initialState: NavigationHistoryState = {
  history: [],
  savedEntityId: null,
  maxHistorySize: 50,
};

/**
 * Payload interface for pushing a new route to navigation history.
 */
interface PushToHistoryPayload {
  route: string;
  key: string;
  state?: Record<string, unknown>;
}

/**
 * Payload interface for navigation with optional entity ID persistence.
 */
interface NavigateWithHistoryPayload {
  route: string;
  key: string;
  state?: Record<string, unknown>;
  savedEntityId?: string;
}

const navigationHistorySlice = createSlice({
  name: 'navigationHistory',
  initialState,
  reducers: {
    /**
     * Push/refresh the current route entry.
     * If the last entry has the same route, refresh its key/state/timestamp instead of pushing.
     */
    pushToHistory: (state, action: PayloadAction<PushToHistoryPayload>) => {
      const { route, key, state: routeState } = action.payload;
      
      const newEntry: RouteHistoryEntry = {
        route,
        key,
        state: routeState,
        timestamp: Date.now(),
      };

      const lastEntry = state.history[state.history.length - 1];
      if (lastEntry && lastEntry.route === route) {
        lastEntry.key = key;
        lastEntry.state = routeState;
        lastEntry.timestamp = Date.now();
        return;
      }
      state.history.push(newEntry);
      if (state.history.length > state.maxHistorySize) {
        state.history.shift();
      }
    },

    /**
     * Push a route and (optionally) store a savedEntityId used after navigation.
     */
    navigateWithHistory: (state, action: PayloadAction<NavigateWithHistoryPayload>) => {
      const { route, key, state: routeState, savedEntityId } = action.payload;
      
      if (savedEntityId) {
        state.savedEntityId = savedEntityId;
      }
      const newEntry: RouteHistoryEntry = {
        route,
        key,
        state: routeState,
        timestamp: Date.now(),
      };

      state.history.push(newEntry);
      
      if (state.history.length > state.maxHistorySize) {
        state.history.shift();
      }
    },

    /**
     * Remove all occurrences of the provided route from the stack.
     */
    removeCurrentRouteFromHistory: (state, action: PayloadAction<string>) => {
      const currentRoute = action.payload;
      state.history = state.history.filter(entry => entry.route !== currentRoute);
    },

    /**
     * Clear the entire navigation history.
     */
    clearHistory: (state) => {
      state.history = [];
    },

    /**
     * Set a saved entity id to be consumed later by a screen (e.g., to fetch details after navigating).
     */
    setSavedEntityId: (state, action: PayloadAction<string>) => {
      state.savedEntityId = action.payload;
    },

    /**
     * Clear the saved entity id once consumed.
     */
    clearSavedEntityId: (state) => {
      state.savedEntityId = null;
    },

    /**
     * Update max history size and trim older entries if necessary.
     */
    setMaxHistorySize: (state, action: PayloadAction<number>) => {
      state.maxHistorySize = action.payload;
      if (state.history.length > state.maxHistorySize) {
        state.history = state.history.slice(-state.maxHistorySize);
      }
    },

    /**
     * Handle back navigation initiated by the app by removing the current route.
     */
    handleBackNavigation: (state, action: PayloadAction<string>) => {
      const currentRoute = action.payload;
      state.history = state.history.filter(entry => entry.route !== currentRoute);
    },
  },
});
export const {
  pushToHistory,
  navigateWithHistory,
  removeCurrentRouteFromHistory,
  clearHistory,
  setSavedEntityId,
  clearSavedEntityId,
  setMaxHistorySize,
  handleBackNavigation,
} = navigationHistorySlice.actions;

/** Root selector for the entire slice state */
export const selectNavigationHistoryState = (state: { navigationHistory: NavigationHistoryState }) => 
  state.navigationHistory;

/** Selector: the raw list of history entries */
export const selectNavigationHistory = (state: { navigationHistory: NavigationHistoryState }) => 
  state.navigationHistory.history;

/** Selector: the currently stored savedEntityId */
export const selectSavedEntityId = (state: { navigationHistory: NavigationHistoryState }) => 
  state.navigationHistory.savedEntityId;

/** Selector: configured max history size */
export const selectMaxHistorySize = (state: { navigationHistory: NavigationHistoryState }) => 
  state.navigationHistory.maxHistorySize;

/** Selector: convenience boolean for UI enablement */
export const selectHasHistory = (state: { navigationHistory: NavigationHistoryState }) => 
  state.navigationHistory.history.length > 0;

/** Selector: last entry or null */
export const selectLastHistoryEntry = (state: { navigationHistory: NavigationHistoryState }) => {
  const history = state.navigationHistory.history;
  return history.length > 0 ? history[history.length - 1] : null;
};

/** Selector: previous route different from the provided currentRoute */
export const selectPreviousRoute = (state: { navigationHistory: NavigationHistoryState }, currentRoute: string): RouteHistoryEntry | null => {
  const history = state.navigationHistory.history;
  for (let i = history.length - 1; i >= 0; i--) {
    const entry = history[i];
    if (entry.route !== currentRoute) {
      return entry;
    }
  }
  return null;
};

/** Selector: whether we can go back given the currentRoute rules */
export const selectCanGoBack = (state: { navigationHistory: NavigationHistoryState }, currentRoute: string): boolean => {
  const history = state.navigationHistory.history;
  if (history.length === 0) {
    return false;
  }
  
  if (currentRoute === RoutesConstants.DASHBOARD || currentRoute === RoutesConstants.HOME) {
    return history.some(entry => entry.route !== currentRoute);
  }
  
  return true;
};

/** Selector helpers for analytics/UX */
export const selectHistoryEntriesForRoute = (state: { navigationHistory: NavigationHistoryState }, route: string) => 
  state.navigationHistory.history.filter(entry => entry.route === route);

export const selectHistoryExcludingRoute = (state: { navigationHistory: NavigationHistoryState }, route: string) => 
  state.navigationHistory.history.filter(entry => entry.route !== route);

export const selectRecentHistory = (state: { navigationHistory: NavigationHistoryState }, count: number) => 
  state.navigationHistory.history.slice(-count);

export const selectHistoryByTimeRange = (state: { navigationHistory: NavigationHistoryState }, startTime: number, endTime: number) => 
  state.navigationHistory.history.filter(entry => entry.timestamp >= startTime && entry.timestamp <= endTime);

export const selectRouteExistsInHistory = (state: { navigationHistory: NavigationHistoryState }, route: string) => 
  state.navigationHistory.history.some(entry => entry.route === route);

export const selectHistoryStats = (state: { navigationHistory: NavigationHistoryState }) => {
  const history = state.navigationHistory.history;
  return {
    totalEntries: history.length,
    uniqueRoutes: new Set(history.map(entry => entry.route)).size,
    oldestEntry: history.length > 0 ? history[0] : null,
    newestEntry: history.length > 0 ? history[history.length - 1] : null,
    mostVisitedRoute: history.length > 0 
      ? history.reduce((acc, entry) => {
          acc[entry.route] = (acc[entry.route] || 0) + 1;
          return acc;
        }, {} as Record<string, number>)
      : {},
  };
};

const NavigationHistorySlice = {
  reducer: navigationHistorySlice.reducer,
  actions: {
    pushToHistory,
    navigateWithHistory,
    removeCurrentRouteFromHistory,
    clearHistory,
    setSavedEntityId,
    clearSavedEntityId,
    setMaxHistorySize,
    handleBackNavigation,
  },
  selectors: {
    selectNavigationHistoryState,
    selectNavigationHistory,
    selectSavedEntityId,
    selectMaxHistorySize,
    selectHasHistory,
    selectLastHistoryEntry,
    selectPreviousRoute,
    selectCanGoBack,
    selectHistoryEntriesForRoute,
    selectHistoryExcludingRoute,
    selectRecentHistory,
    selectHistoryByTimeRange,
    selectRouteExistsInHistory,
    selectHistoryStats,
  }
};
export default NavigationHistorySlice;
